import { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormItem,
  Input,
  TextArea,
  useForm,
  Message,
  Select,
} from "@/frontend/components/common";
import { subCategoryService, disciplineService } from "@/frontend/services";
import { useNavigate } from "react-router-dom";
import type {
  CreateSubCategoryRequestDTO,
  UpdateSubCategoryRequestDTO,
  DisciplineResponseDTO,
} from "@/shared/dto";

interface SubCategoryFormValues {
  disciplineId: string;
  category: string;
  externalId?: string;
  description?: string;
}

interface Props {
  subCategoryId?: string;
  initialValues?: Partial<SubCategoryFormValues>;
}

export function SubCategoryForm({ subCategoryId, initialValues }: Props) {
  const navigate = useNavigate();
  const [form] = useForm();
  const isEditMode = !!subCategoryId;
  const [disciplines, setDisciplines] = useState<DisciplineResponseDTO[]>([]);
  const [loadingDisciplines, setLoadingDisciplines] = useState(false);

  useEffect(() => {
    async function loadDisciplines() {
      setLoadingDisciplines(true);
      try {
        const res = await disciplineService.getPaginate({ page: 1, pageSize: 100 });
        if (res.success && res.data) {
          setDisciplines(res.data.data);
        }
      } finally {
        setLoadingDisciplines(false);
      }
    }
    loadDisciplines();
  }, []);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, form]);

  const handleSubmit = async (values: SubCategoryFormValues) => {
    try {
      if (isEditMode && subCategoryId) {
        const updateData: UpdateSubCategoryRequestDTO = {
          disciplineId: values.disciplineId,
          category: values.category,
          externalId: values.externalId || undefined,
          description: values.description || undefined,
        };
        const res = await subCategoryService.update(subCategoryId, updateData);
        if (res.success) {
          Message.success({ content: "Subcategoría actualizada correctamente" });
          navigate("/sub-categories");
        } else {
          Message.error({ content: res.error || "Error al actualizar" });
        }
      } else {
        const createData: CreateSubCategoryRequestDTO = {
          disciplineId: values.disciplineId,
          category: values.category,
          externalId: values.externalId || undefined,
          description: values.description || undefined,
        };
        const res = await subCategoryService.create(createData);
        if (res.success) {
          Message.success({ content: "Subcategoría creada correctamente" });
          navigate("/sub-categories");
        } else {
          Message.error({ content: res.error || "Error al crear" });
        }
      }
    } catch (err) {
      Message.error({ content: err instanceof Error ? err.message : "Error inesperado" });
    }
  };

  return (
    <Form form={form} onFinish={handleSubmit} layout="vertical" className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormItem
          name="disciplineId"
          label="Deporte / Disciplina"
          rules={[{ required: true, message: "La disciplina es requerida" }]}
        >
          <Select
            placeholder="Seleccionar deporte"
            loading={loadingDisciplines}
            size="large"
            showSearch
            options={disciplines.map((d) => ({ label: d.name, value: d.id }))}
          />
        </FormItem>

        <FormItem
          name="category"
          label="Categoría"
          rules={[{ required: true, message: "La categoría es requerida" }]}
        >
          <Input placeholder="Ej: Sub-17, Femenino, Amateur" size="large" />
        </FormItem>

        <FormItem
          name="externalId"
          label="ID de API Externa (Opcional)"
          tooltip="Si es fútbol, usa el ID de liga de API-Sports (ej: 39 para Premier League)"
        >
          <Input placeholder="Ej: 39, 140, etc." size="large" />
        </FormItem>

        <FormItem name="description" label="Descripción" className="md:col-span-2">
          <TextArea placeholder="Descripción breve (opcional)" rows={3} size="large" />
        </FormItem>
      </div>

      <div className="flex justify-end gap-4 mt-6">
        <Button type="primary" htmlType="submit" size="large">
          {isEditMode ? "Actualizar Subcategoría" : "Crear Subcategoría"}
        </Button>
        <Button size="large" onClick={() => navigate("/sub-categories")}>
          Cancelar
        </Button>
      </div>
    </Form>
  );
}
